import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SiteEditorPanel, WorksManager, HistoryPanel, RecruitPanel } from "./AdminSiteEditor";
import { DEFAULT_CONTENT } from "@/lib/siteDefaults";
import { ContractsPanel } from "@/components/admin/ContractsPanel";
import { ApplicationsPanel } from "@/components/admin/ApplicationsPanel";
import { MailPanel } from "@/components/admin/MailPanel";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Trash2,
  RefreshCw,
  Loader2,
  LayoutDashboard,
  Briefcase,
  History as HistoryIcon,
  Image as ImageIcon,
  Eye,
  Search,
  Upload,
  FileText,
  Users,
  UserPlus,
  Inbox,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const statusMeta = {
  new: { label: "未対応", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  read: { label: "確認済", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  replied: { label: "対応済", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
};

const PANELS = [
  { id: "contacts", label: "お問い合わせ", icon: Mail },
  { id: "site", label: "サイト編集", icon: LayoutDashboard },
  { id: "recruit", label: "採用情報", icon: Users },
  { id: "applications", label: "応募受付", icon: UserPlus },
  { id: "mail", label: "メール", icon: Inbox },
  { id: "works", label: "開発実績", icon: Briefcase },
  { id: "history", label: "変更履歴", icon: HistoryIcon },
  { id: "media", label: "画像ライブラリ", icon: ImageIcon },
  { id: "contracts", label: "電子契約", icon: FileText },
];

function FunctionCard({ icon: Icon, label, value, badges = [], active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-xl border border-l-2 p-5 transition-all duration-300 hover:-translate-y-0.5 ${
        active
          ? "border-gold/50 border-l-gold bg-gold/[0.07]"
          : "border-white/10 border-l-gold/40 bg-white/[0.02] hover:bg-white/[0.05] hover:border-gold/30"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-white/50 truncate">{label}</p>
          <p className="mt-2 font-display text-3xl font-extrabold text-white leading-none">{value}</p>
        </div>
        <Icon size={22} className="text-gold/70 shrink-0 mt-0.5" />
      </div>
      {badges.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {badges.map((b, i) => (
            <span key={i} className={`rounded border px-1.5 py-0.5 text-[10px] whitespace-nowrap ${b.cls || "bg-white/5 text-white/50 border-white/10"}`}>
              {b.label}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

function MediaPanel() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/media").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("画像をアップロードしました");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/media/${id}`);
      setItems((x) => x.filter((m) => m.id !== id));
      toast.success("削除しました");
    } catch {
      toast.error("削除に失敗");
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${url}`);
      toast.success("画像URLをコピーしました");
    } catch {
      toast.error("コピーに失敗しました");
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="media-panel">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-white/50">クリックで画像URLをコピーできます</p>
        <button
          onClick={() => ref.current?.click()}
          disabled={busy}
          data-testid="media-upload-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-medium text-ink hover:bg-gold-light transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} 画像をアップロード
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
      </div>
      {items === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-gold" /></div>
      ) : items.length === 0 ? (
        <p className="py-16 text-center text-white/40 text-sm" data-testid="media-empty">アップロード済みの画像はありません。</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {items.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => copy(m.url)}
              data-testid={`media-${m.id}`}
              className="group relative aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-gold transition-colors"
            >
              <img src={m.url} alt="" className="h-full w-full object-cover" />
              <span
                onClick={(e) => del(m.id, e)}
                data-testid={`media-delete-${m.id}`}
                className="absolute top-1 right-1 grid place-items-center h-6 w-6 rounded bg-black/60 text-white/70 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 size={13} />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, replied: 0 });
  const [counts, setCounts] = useState({ works: 0, versions: 0, media: 0, contracts: 0, contractsPending: 0 });
  const [appStats, setAppStats] = useState({ total: 0, new: 0, replied: 0 });
  const [mailCount, setMailCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [panel, setPanel] = useState("contacts");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (ready && !user) navigate("/admin/login");
  }, [ready, user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s, w, v, m, ct, d, as] = await Promise.all([
        api.get("/contact"),
        api.get("/contact/stats"),
        api.get("/works"),
        api.get("/content/versions"),
        api.get("/media"),
        api.get("/contracts"),
        api.get("/content/draft"),
        api.get("/applications/stats"),
      ]);
      setContacts(c.data);
      setStats(s.data);
      setCounts({
        works: w.data.length,
        versions: v.data.length,
        media: m.data.length,
        contracts: ct.data.length,
        contractsPending: ct.data.filter((x) => x.status !== "signed").length,
        contractsSigned: ct.data.filter((x) => x.status === "signed").length,
        recruitPositions: d.data?.recruit?.positions?.length ?? DEFAULT_CONTENT.recruit.positions.length,
      });
      setAppStats(as.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ready && user) load();
    // eslint-disable-next-line
  }, [ready, user]);

  useEffect(() => {
    if (ready && user) {
      api.get("/mail/messages").then((r) => setMailCount(r.data.length)).catch(() => setMailCount(null));
    }
    // eslint-disable-next-line
  }, [ready, user]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/contact/${id}/status`, { status });
      toast.success("ステータスを更新しました");
      load();
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/contact/${id}`);
      toast.success("削除しました");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) =>
      [c.name, c.email, c.service, c.message, c.company].filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [contacts, query]);

  const openPreview = () => window.open("/?preview=1", "_blank", "noopener");

  if (!ready || !user) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <Loader2 className="animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 sticky top-0 z-20 bg-black/60 backdrop-blur-2xl">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-2 font-display text-lg font-extrabold tracking-tight"><img src="/prc-logo.png" alt="" className="h-7 w-7 object-contain" />PRC<span className="text-gold"> REMIT</span></span>
            <span className="text-xs text-white/40 border-l border-white/10 pl-4">管理画面</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/50 hidden sm:inline">{user.name || user.email}</span>
            <button onClick={load} data-testid="refresh-btn" className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-3.5 py-1.5 text-sm text-white/70 hover:text-gold hover:border-gold/40 transition-colors">
              <RefreshCw size={15} /> 更新
            </button>
            <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="logout-btn" className="inline-flex items-center gap-2 rounded-lg border border-red-400/40 px-3.5 py-1.5 text-sm text-red-300 hover:bg-red-400/10 transition-colors">
              <LogOut size={15} /> ログアウト
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8 pb-32">
        {/* 機能カード */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="function-cards">
          <FunctionCard
            icon={Mail} label="お問い合わせ" value={stats.total} active={panel === "contacts"}
            onClick={() => setPanel("contacts")} testid="card-contacts"
            badges={[
              { label: `未対応: ${stats.new}`, cls: "bg-amber-500/10 text-amber-300 border-amber-500/20" },
              { label: `対応済: ${stats.replied}`, cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20" },
            ]}
          />
          <FunctionCard
            icon={LayoutDashboard} label="サイト編集" value={8} active={panel === "site"}
            onClick={() => setPanel("site")} testid="card-site"
            badges={[{ label: "全セクション" }]}
          />
          <FunctionCard
            icon={Users} label="採用情報" value={counts.recruitPositions} active={panel === "recruit"}
            onClick={() => setPanel("recruit")} testid="card-recruit"
            badges={[{ label: "募集職種" }]}
          />
          <FunctionCard
            icon={UserPlus} label="応募受付" value={appStats.total} active={panel === "applications"}
            onClick={() => setPanel("applications")} testid="card-applications"
            badges={[
              { label: `未対応: ${appStats.new}`, cls: "bg-amber-500/10 text-amber-300 border-amber-500/20" },
              { label: `対応済: ${appStats.replied}`, cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20" },
            ]}
          />
          <FunctionCard
            icon={Inbox} label="メール" value={mailCount === null ? "—" : mailCount} active={panel === "mail"}
            onClick={() => setPanel("mail")} testid="card-mail"
            badges={[{ label: "受信・送信・設定" }]}
          />
          <FunctionCard
            icon={Briefcase} label="開発実績" value={counts.works} active={panel === "works"}
            onClick={() => setPanel("works")} testid="card-works"
            badges={[{ label: "登録済み" }]}
          />
          <FunctionCard
            icon={HistoryIcon} label="変更履歴" value={counts.versions} active={panel === "history"}
            onClick={() => setPanel("history")} testid="card-history"
            badges={[{ label: "復元可能" }]}
          />
          <FunctionCard
            icon={ImageIcon} label="画像ライブラリ" value={counts.media} active={panel === "media"}
            onClick={() => setPanel("media")} testid="card-media"
            badges={[{ label: "アップロード済" }]}
          />
          <FunctionCard
            icon={FileText} label="電子契約" value={counts.contracts} active={panel === "contracts"}
            onClick={() => setPanel("contracts")} testid="card-contracts"
            badges={[
              { label: `署名待ち: ${counts.contractsPending}`, cls: "bg-amber-500/10 text-amber-300 border-amber-500/20" },
              { label: `契約済: ${counts.contractsSigned ?? 0}`, cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20" },
            ]}
          />
          <FunctionCard
            icon={Eye} label="サイトプレビュー" value="確認" onClick={openPreview} testid="card-preview"
            badges={[{ label: "別タブで開く" }]}
          />
        </div>

        {/* 検索 */}
        <div className="mt-8 relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="search-input"
            placeholder="名前、メール、件名で検索…"
            className="w-full rounded-xl border border-white/10 bg-white/[0.02] pl-11 pr-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 outline-none transition-colors"
          />
        </div>

        {/* クイック操作 */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="quick-nav">
          {PANELS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setPanel(id)}
              data-testid={`nav-${id}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs transition-colors ${
                panel === id
                  ? "border-gold/60 bg-gold/10 text-gold"
                  : "border-white/10 text-white/50 hover:text-white hover:border-white/25"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
          <button onClick={openPreview} data-testid="nav-preview" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3.5 py-2 text-xs text-white/50 hover:text-white hover:border-white/25 transition-colors">
            <Eye size={14} /> プレビュー
          </button>
        </div>

        {/* パネル */}
        <div className="mt-6">
          {panel === "contacts" && (
            loading ? (
              <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>
            ) : filtered.length === 0 ? (
              <div className="py-20 text-center text-white/40" data-testid="empty-state">
                {contacts.length === 0 ? "まだお問い合わせはありません。" : "条件に一致するお問い合わせはありません。"}
              </div>
            ) : (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-x-auto" data-testid="contacts-table">
                <table className="w-full min-w-[760px] text-sm">
                  <thead>
                    <tr className="text-left text-xs text-white/40 border-b border-white/10">
                      <th className="px-5 py-3.5 font-medium">日時</th>
                      <th className="px-2 py-3.5 font-medium">氏名</th>
                      <th className="px-2 py-3.5 font-medium">メール</th>
                      <th className="px-2 py-3.5 font-medium">件名</th>
                      <th className="px-2 py-3.5 font-medium">ステータス</th>
                      <th className="px-5 py-3.5 font-medium text-right">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c) => (
                      <tr
                        key={c.id}
                        data-testid={`contact-row-${c.id}`}
                        onClick={() => setSelected(c)}
                        className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] cursor-pointer transition-colors"
                      >
                        <td className="px-5 py-3.5 text-white/50 whitespace-nowrap">
                          {new Date(c.created_at).toLocaleString("ja-JP", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="px-2 py-3.5 font-jp font-bold text-white">{c.name}</td>
                        <td className="px-2 py-3.5 text-white/60">{c.email}</td>
                        <td className="px-2 py-3.5 text-white/60">{c.service}</td>
                        <td className="px-2 py-3.5">
                          <Badge variant="outline" className={statusMeta[c.status]?.cls}>{statusMeta[c.status]?.label}</Badge>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center justify-end gap-3">
                            <button onClick={(e) => { e.stopPropagation(); setSelected(c); }} data-testid={`view-${c.id}`} className="text-white/40 hover:text-gold transition-colors"><Eye size={16} /></button>
                            <button onClick={(e) => { e.stopPropagation(); remove(c.id); }} data-testid={`delete-${c.id}`} className="text-white/40 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {panel === "site" && <SiteEditorPanel />}

          {panel === "recruit" && <RecruitPanel />}

          {panel === "applications" && <ApplicationsPanel query={query} onChanged={load} />}

          {panel === "mail" && <MailPanel />}

          {panel === "works" && (
            <div className="max-w-4xl mx-auto"><WorksManager /></div>
          )}

          {panel === "history" && (
            <div className="max-w-4xl mx-auto rounded-xl border border-white/10 bg-white/[0.02] p-5">
              <HistoryPanel />
            </div>
          )}

          {panel === "media" && <MediaPanel />}

          {panel === "contracts" && <ContractsPanel onChanged={load} />}
        </div>
      </main>

      {selected && (
        <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setSelected(null)} data-testid="detail-modal">
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-surface p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-white/40 mb-2">{selected.service}</p>
                <h3 className="font-jp text-2xl font-bold">{selected.name}</h3>
              </div>
              <Badge variant="outline" className={statusMeta[selected.status]?.cls}>{statusMeta[selected.status]?.label}</Badge>
            </div>
            <dl className="space-y-3 text-sm">
              <div className="flex gap-4"><dt className="w-24 text-white/40 shrink-0">メール</dt><dd className="text-white/90 break-all">{selected.email}</dd></div>
              {selected.company && <div className="flex gap-4"><dt className="w-24 text-white/40 shrink-0">会社名</dt><dd className="text-white/90">{selected.company}</dd></div>}
              <div className="flex gap-4"><dt className="w-24 text-white/40 shrink-0">受信</dt><dd className="text-white/90">{new Date(selected.created_at).toLocaleString("ja-JP")}</dd></div>
              <div className="pt-2"><dt className="text-white/40 mb-2">メッセージ</dt><dd className="text-white/90 leading-relaxed whitespace-pre-wrap bg-white/[0.03] rounded-lg p-4">{selected.message}</dd></div>
            </dl>
            <div className="mt-7 flex items-center gap-3">
              <Select value={selected.status} onValueChange={(v) => changeStatus(selected.id, v)}>
                <SelectTrigger className="flex-1 bg-white/[0.03] border-white/10" data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">未対応</SelectItem>
                  <SelectItem value="read">確認済</SelectItem>
                  <SelectItem value="replied">対応済</SelectItem>
                </SelectContent>
              </Select>
              <button onClick={() => setSelected(null)} className="rounded-lg border border-white/10 px-5 py-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="close-modal">閉じる</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
